import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../components/ui/Modal";
import QuickEntryForm from "../components/forms/QuickEntryForm";
import {
  UserGroupIcon,
  SparklesIcon,
  TrophyIcon,
  CheckCircleIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../components/layout/PageHeader";
import { StatCard } from "../components/ui/Card";
import { useAuth } from "../hooks/useAuth";
import { SESSION_DISPLAY_LABEL, useSeason } from "../context/SeasonContext";
import { formatSessionTotalsLine } from "../utils/sessionTotals";
import toast from "react-hot-toast";
import api from "../api/axios";

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

interface TermCount {
  termId: number;
  termName: string;
  count: number;
}

interface DashboardStats {
  campersThisYear: number;
  campersAllTime: number;
  totalActivities: number;
  completionsThisYear: number;
  completionsAllTime: number;
  honorsThisYear: number;
  honorsAllTime: number;
  termCounts: TermCount[];
  fullTermCount: number;
}

interface ActivityFeedItem {
  type: string;
  camperName: string;
  description: string;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedYear, selectedTermId, selectedTerm, activeSession } = useSeason();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [feed, setFeed] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [quickEntryMode, setQuickEntryMode] = useState<"ac" | "honor" | "graduation" | null>(null);

  const buildParams = () => {
    const params: Record<string, string> = { year: String(selectedYear) };
    if (selectedTermId) params.termId = String(selectedTermId);
    return params;
  };

  useEffect(() => {
    setLoading(true);
    const params = buildParams();
    api
      .get<DashboardStats>("/dashboard/stats", { params })
      .then((res) => setStats(res.data))
      .catch(() => toast.error("Failed to load dashboard stats"))
      .finally(() => setLoading(false));

    api
      .get<ActivityFeedItem[]>("/dashboard/activity", { params })
      .then((res) => setFeed(res.data))
      .catch(() => toast.error("Failed to load recent activity"));
  }, [selectedYear, selectedTermId]);

  const refreshDashboard = () => {
    const params = buildParams();
    api.get<DashboardStats>("/dashboard/stats", { params }).then((res) => setStats(res.data)).catch(() => {});
    api.get<ActivityFeedItem[]>("/dashboard/activity", { params }).then((res) => setFeed(res.data)).catch(() => {});
  };

  const openQuickEntry = (mode: "ac" | "honor" | "graduation") => {
    if (!selectedTermId) {
      toast.error("Please select a specific session before recording.");
      return;
    }
    setQuickEntryMode(mode);
  };

  const sessionLabel = SESSION_DISPLAY_LABEL[activeSession];

  // Active-session camper count: prefer the term-specific row from termCounts
  // when one matches; fall back to the year-level total. termCounts comes
  // back from /dashboard/stats, scoped to the selected year.
  const sessionCamperCount = useMemo(() => {
    if (!stats) return null;
    if (selectedTermId) {
      const row = stats.termCounts.find((t) => t.termId === selectedTermId);
      if (row) return row.count;
    }
    return stats.campersThisYear;
  }, [stats, selectedTermId]);

  // Spec format: "1st Term: 187 (1st Term only: 92 · Full Term: 95)"
  // For Rangers: just "Rangers: N" — no full-term split.
  const camperSubValue = useMemo(() => {
    if (!stats || sessionCamperCount == null) return sessionLabel;
    const totals = {
      total: sessionCamperCount,
      fullTerm: stats.fullTermCount,
      sessionOnly: Math.max(0, sessionCamperCount - stats.fullTermCount),
    };
    return formatSessionTotalsLine(activeSession, totals);
  }, [stats, sessionCamperCount, activeSession, sessionLabel]);

  return (
    <div>
      <PageHeader
        title={`${greeting()}, ${user?.firstName}!`}
        subtitle={`${selectedYear} · ${sessionLabel} · Camp Highlands Achievement Tracking`}
      />

      <div className="p-6 lg:p-8 space-y-8">
        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard
            label="Campers"
            value={loading || sessionCamperCount == null ? "—" : sessionCamperCount}
            subValue={loading ? sessionLabel : camperSubValue}
            icon={<UserGroupIcon className="w-5 h-5" />}
            color="green"
          />
          <StatCard
            label="Activities"
            value={loading || !stats ? "—" : stats.totalActivities}
            subValue="Active in catalog"
            icon={<SparklesIcon className="w-5 h-5" />}
            color="amber"
          />
          <StatCard
            label="ACs Earned"
            value={loading || !stats ? "—" : stats.completionsThisYear}
            subValue={loading || !stats ? sessionLabel : `${sessionLabel} · ${stats.completionsAllTime} all time`}
            icon={<CheckCircleIcon className="w-5 h-5" />}
            color="bark"
          />
          <StatCard
            label="Honors"
            value={loading || !stats ? "—" : stats.honorsThisYear}
            subValue={loading || !stats ? sessionLabel : `${sessionLabel} · ${stats.honorsAllTime} all time`}
            icon={<TrophyIcon className="w-5 h-5" />}
            color="green"
          />
        </div>

        {/* Quick actions */}
        <div>
          <h2 className="text-base font-semibold text-stone-700 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <button
              onClick={() => openQuickEntry("ac")}
              className="bg-forest-600 hover:bg-forest-700 text-white rounded-xl p-5 transition-colors shadow-sm block text-left w-full"
            >
              <p className="font-semibold text-sm">Record AC</p>
            </button>
            <button
              onClick={() => openQuickEntry("graduation")}
              className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl p-5 transition-colors shadow-sm block text-left w-full"
            >
              <div className="flex items-center gap-1.5">
                <AcademicCapIcon className="w-4 h-4" />
                <p className="font-semibold text-sm">Record Graduation</p>
              </div>
            </button>
            <button
              onClick={() => openQuickEntry("honor")}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl p-5 transition-colors shadow-sm block text-left w-full"
            >
              <p className="font-semibold text-sm">Award an Honor</p>
            </button>
            <Link
              to="/reports/kerchief"
              className="bg-bark-600 hover:bg-bark-700 text-white rounded-xl p-5 transition-colors shadow-sm block"
            >
              <p className="font-semibold text-sm">View Kerchief</p>
            </Link>
          </div>
        </div>

        {/* Quick Entry Modal */}
        <Modal
          open={quickEntryMode !== null}
          onClose={() => setQuickEntryMode(null)}
          title={quickEntryMode === "ac" ? "Record AC" : quickEntryMode === "graduation" ? "Record Graduation" : "Award an Honor"}
          size="md"
        >
          {quickEntryMode && (
            <QuickEntryForm
              mode={quickEntryMode}
              onSuccess={() => {
                setQuickEntryMode(null);
                refreshDashboard();
              }}
              onCancel={() => setQuickEntryMode(null)}
            />
          )}
        </Modal>

        {/* Recent activity feed */}
        <div>
          <h2 className="text-base font-semibold text-stone-700 mb-3">Recent Activity</h2>
          <div className="camp-card divide-y divide-stone-100">
            {feed.length === 0 ? (
              <div className="px-5 py-4 text-sm text-stone-400">No recent activity yet.</div>
            ) : (
              feed.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <span className="text-sm font-medium text-stone-800">{item.camperName}</span>
                    <span className="text-sm text-stone-500"> {item.description}</span>
                  </div>
                  <span className="text-xs text-stone-400 flex-shrink-0 ml-4">{timeAgo(item.createdAt)}</span>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

